import { useCallback, useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { AppShell } from "../components/layout/AppShell";
import { Card, Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Input, Select } from "../components/ui/Fields";
import { DataTable, type Column } from "../components/ui/DataTable";
import { ConfirmDialog, Modal } from "../components/ui/Modal";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";

/**
 * Round 45F — manage a user's Backpack (user_mall_items): find the user by
 * public User ID, see every item (name, category, quantity owned, expiry,
 * equipped state), then remove it, change its duration, or unequip it.
 * Only item data is shown — no email, balance or other account data.
 * Over routes/admin/user-backpack.js (super_admin only, audit-logged).
 */
interface FoundUser {
  id: string;
  user_id: number;
  username: string;
  avatar_url: string | null;
}

interface BackpackItem {
  id: number;
  itemId: number;
  name: string;
  category: string | null;
  slotType: string | null;
  imageUrl: string | null;
  quantity: number;
  acquiredAt: string;
  expiresAt: string | null;
  isEquipped: boolean;
  isExpired: boolean;
}

const DURATION_CHOICES = [
  { label: "1 day from now", value: "1" },
  { label: "7 days from now", value: "7" },
  { label: "30 days from now", value: "30" },
  { label: "90 days from now", value: "90" },
  { label: "365 days from now", value: "365" },
  { label: "Permanent (no expiry)", value: "permanent" },
];

export function UserBackpackPage() {
  const [searchId, setSearchId] = useState("");
  const [found, setFound] = useState<FoundUser | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const [items, setItems] = useState<BackpackItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [durationTarget, setDurationTarget] = useState<BackpackItem | null>(null);
  const [duration, setDuration] = useState("30");
  const [deleteTarget, setDeleteTarget] = useState<BackpackItem | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const { showToast } = useToast();

  const loadItems = useCallback(async (profileId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      setItems(await apiRequest<BackpackItem[]>(`/admin/user-backpack/${profileId}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load backpack");
    } finally {
      setIsLoading(false);
    }
  }, []);

  async function handleSearch() {
    if (!searchId.trim()) return;
    setIsSearching(true);
    setSearchError(null);
    setFound(null);
    setItems([]);
    try {
      const user = await apiRequest<FoundUser>("/admin/user-backpack/lookup", { params: { query: searchId.trim() } });
      setFound(user);
      await loadItems(user.id);
    } catch (err) {
      setSearchError(err instanceof ApiError ? err.message : "Lookup failed");
    } finally {
      setIsSearching(false);
    }
  }

  async function patchItem(item: BackpackItem, body: Record<string, unknown>, doneMessage: string) {
    setIsBusy(true);
    try {
      await apiRequest(`/admin/user-backpack/items/${item.id}`, { method: "PATCH", body });
      showToast(doneMessage, "success");
      if (found) await loadItems(found.id);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Update failed", "error");
    } finally {
      setIsBusy(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setIsBusy(true);
    try {
      await apiRequest(`/admin/user-backpack/items/${deleteTarget.id}`, { method: "DELETE" });
      showToast("Item removed from backpack", "success");
      setDeleteTarget(null);
      if (found) await loadItems(found.id);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not remove", "error");
    } finally {
      setIsBusy(false);
    }
  }

  const columns: Column<BackpackItem>[] = [
    {
      header: "Item",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.imageUrl ? <img src={row.imageUrl} alt="" className="h-9 w-9 rounded object-cover" /> : <div className="h-9 w-9 rounded bg-surface-raised" />}
          <span className="text-sm">{row.name}</span>
        </div>
      ),
    },
    { header: "Category", render: (row) => <span className="text-xs">{row.category ?? row.slotType ?? "—"}</span> },
    { header: "Qty", render: (row) => row.quantity },
    { header: "Acquired", render: (row) => new Date(row.acquiredAt).toLocaleDateString() },
    {
      header: "Expires",
      render: (row) =>
        row.expiresAt ? (
          <span className={row.isExpired ? "text-danger" : ""}>
            {new Date(row.expiresAt).toLocaleString()}
            {row.isExpired ? " (expired)" : ""}
          </span>
        ) : (
          "Permanent"
        ),
    },
    {
      header: "State",
      render: (row) => (row.isEquipped && !row.isExpired ? <Badge tone="success">Equipped</Badge> : <Badge tone="neutral">Not equipped</Badge>),
    },
    {
      header: "",
      render: (row) => (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setDurationTarget(row)} disabled={isBusy}>
            Edit duration
          </Button>
          {row.isEquipped && (
            <Button variant="secondary" onClick={() => patchItem(row, { unequip: true }, "Item unequipped")} disabled={isBusy}>
              Unequip
            </Button>
          )}
          <Button variant="secondary" onClick={() => setDeleteTarget(row)} disabled={isBusy}>
            Remove
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="User Backpack">
      <p className="mb-4 max-w-2xl text-xs text-ink-faint">
        Find a user by public User ID to see what is in their Backpack and remove an item, change how long it lasts, or
        unequip it. Quantity is the number of copies of that item the user owns. Every change is recorded in the audit log.
      </p>

      <Card className="max-w-2xl p-5">
        <div className="flex items-end gap-2">
          <Input
            label="Public User ID"
            placeholder="e.g. 74848506"
            value={searchId}
            onChange={(e) => {
              setSearchId(e.target.value);
              setSearchError(null);
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <Button variant="secondary" onClick={handleSearch} isLoading={isSearching} disabled={!searchId.trim()}>
            Find
          </Button>
        </div>
        {searchError && <p className="mt-1.5 text-xs text-danger">{searchError}</p>}
        {found && (
          <div className="mt-4 flex items-center gap-3 border-t border-surface-border pt-4">
            <Avatar src={found.avatar_url} className="h-10 w-10 rounded-full object-cover" />
            <div>
              <p className="font-semibold text-ink">{found.username}</p>
              <p className="font-mono text-xs text-ink-faint">ID: {found.user_id}</p>
            </div>
          </div>
        )}
      </Card>

      {found && (
        <div className="mt-4 max-w-6xl">
          <DataTable
            columns={columns}
            rows={items}
            keyField={(r) => r.id}
            isLoading={isLoading}
            error={error}
            onRetry={() => loadItems(found.id)}
            emptyTitle="This user's backpack is empty"
          />
        </div>
      )}

      <Modal
        isOpen={!!durationTarget}
        onClose={() => !isBusy && setDurationTarget(null)}
        title={durationTarget ? `Edit duration — ${durationTarget.name}` : "Edit duration"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDurationTarget(null)} disabled={isBusy}>
              Cancel
            </Button>
            <Button
              isLoading={isBusy}
              onClick={async () => {
                if (!durationTarget) return;
                await patchItem(durationTarget, { durationDays: duration === "permanent" ? "permanent" : Number(duration) }, "Duration updated");
                setDurationTarget(null);
              }}
            >
              Save
            </Button>
          </>
        }
      >
        <Select label="New duration (counted from now)" value={duration} onChange={(e) => setDuration(e.target.value)}>
          {DURATION_CHOICES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </Select>
        {durationTarget && (
          <p className="mt-2 text-xs text-ink-faint">
            Current expiry: {durationTarget.expiresAt ? new Date(durationTarget.expiresAt).toLocaleString() : "Permanent"}
          </p>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => !isBusy && setDeleteTarget(null)}
        onConfirm={handleDelete}
        isLoading={isBusy}
        isDangerous
        title="Remove item"
        confirmLabel="Remove"
        description={deleteTarget ? `Remove "${deleteTarget.name}" from this user's backpack? This cannot be undone.` : ""}
      />
    </AppShell>
  );
}
